import { Injectable, OnDestroy } from "@angular/core";
import { HttpHeaders, HttpClient } from "@angular/common/http";

import { takeUntil, tap } from "rxjs/operators";
import { BehaviorSubject, Observable, Subject } from "rxjs";

import { StorageKeyEnum } from "../core/StorageKeyEnum";
import { LoginModel } from "../models/login.model";
import {
  AccountModel,
  BillingInfoModel,
  AddWalmartAccount,
  SubscriptionStatus,
  AddAmazonAccountParams,
  AddWooCommerceAccount,
  AddBigCommercePayload,
} from "../models/onboarding.model";

import { AuthService } from "./auth.service";

@Injectable({
  providedIn: "root",
})
export class OnboardingService implements OnDestroy {
  private url = "api/onboarding";
  private headers = new HttpHeaders({ "Content-Type": "application/json" });
  private destroy$ = new Subject<void>();

  options = { headers: this.headers };
  price: number;
  onboardingStep: number;
  tiers
  userInfo: LoginModel;
  pauseSubj$ = new BehaviorSubject<boolean>(false);
  isDisabled = new BehaviorSubject<boolean>(true);
  isSpin = new BehaviorSubject<boolean>(false);
  constructor(private http: HttpClient, private authService: AuthService) {
    this.authService.userInfo$.pipe(takeUntil(this.destroy$)).subscribe((data) => (this.userInfo = data));
  }

  public InsertAccount(accountModel: AccountModel) {
    const data = JSON.stringify(accountModel);
    return this.http.post(`/${this.url}/account`, data, {
      headers: this.headers,
      responseType: "text",
    });
  }

  public UpdateAccount(accountModel: AccountModel) {
    const data = JSON.stringify(accountModel);
    return this.http.put(`/${this.url}/account`, data, this.options);
  }

  public InsertBillingInfo(billingInfoModel: BillingInfoModel) {
    const data = JSON.stringify(billingInfoModel);
    return this.http.post(`/${this.url}/addbillinginfo`, data, {
      headers: this.headers,
      observe: "response",
      responseType: "text",
    });
  }

  public InsertWarehouse(warehouseModel) {
    const data = JSON.stringify(warehouseModel);
    return this.http.post(`/${this.url}/addwh`, data, this.options);
  }

  public AmazonLink() {
    return this.http.get(`/${this.url}/amazonlink`, {
      headers: this.headers,
      responseType: "text",
    });
  }

  public AmazonMarkets() {
    return this.http.get(`/${this.url}/amazonmarkets`, this.options);
  }

  amazonMarketsByRegion(amazonMarketRegionId){
    return this.http.get(`/${this.url}/AmazonMarketsByRegion?amazonMarketRegionId=${amazonMarketRegionId}`, this.options);
  }

  public DuplicateAccountName(value: string) {
    return this.http.get(
      `/${this.url}/DuplicateAccountName?value=${value}`,
      this.options
    );
  }

  public UpdateOnboardingStep(step: number) {
    const user = JSON.parse(
      localStorage.getItem(StorageKeyEnum.User)
    ) as LoginModel;
    return this.http
      .get(
        `/${this.url}/updateonboardingstep/${user.userId}/${step}`,
        this.options
      )
      .pipe(
        tap(() => {
          let newUserObject = user;
          newUserObject["onboardingStep"] = step;
          localStorage.setItem(
            StorageKeyEnum.User,
            JSON.stringify(newUserObject)
          );
        })
      );
  }

  public verifyCredential(verifycredential: any) {
    return this.http.post(
      `/${this.url}/verifycredential`,
      verifycredential,
      this.options
    );
  }

  public getStates() {
    return this.http.get("./assets/states.json");
  }

  public SaveProductsSelected(products: string[]) {
    return this.http.post(
      `/${this.url}/SaveProductsSelected`,
      { products },
      this.options
    );
  }

  public AddWalmartAccount(data: AddWalmartAccount) {
    return this.http.post(`/${this.url}/AddWalmartAccount`, data, this.options);
  }

  public LeaveFeedback(data: { userId: number; message: string }) {
    return this.http.post(`/${this.url}/LeaveFeedback`, data, this.options);
  }

  public ListProducts() {
    return this.http.post(`/${this.url}/ListProducts`, this.options);
  }

  public SaveTierSelection(body: { tiers: number[] }) {
    return this.http.post(`/${this.url}/SaveTierSelection`, body, this.options);
  }

  public SaveBillingInfo(body: {
    name: string;
    creditCardNumber: string;
    expirationDate: string;
    cvc: string;
  }) {
    const year = body.expirationDate.split("/")[1].trim();
    if (year.length === 4) {
      const newYear = year.substring(2);
      body.expirationDate =
        body.expirationDate.substring(0, body.expirationDate.length - 4) +
        newYear;
    }
    return this.http.post(`/${this.url}/SaveBillingInfo`, body, this.options);
  }

  public GetInvoices() {
    return this.http.post(`/${this.url}/GetInvoices`, {}, this.options);
  }

  public GetSubscriptionStatus(): Observable<SubscriptionStatus> {
    return this.http.post<SubscriptionStatus>(
      `/${this.url}/GetSubscriptionStatus`,
      {},
      this.options
    );
  }
  public GetCreditCards() {
    return this.http.post(`/${this.url}/GetCreditCards `, {}, this.options);
  }
  public PriceDiff(tiers: number[]) {
    return this.http.post(`/${this.url}/PriceDiff `, { tiers }, this.options);
  }
  public SellerExists(seller) {
    return this.http.post(`/${this.url}/SellerExists `, seller, this.options);
  }

  UpgradeToPremium() {
    return this.http.post(`/${this.url}/UpgradeToPremium `, {}, this.options);
  }

  PurchaseFreemiumCredit() {
    return this.http.post(`/${this.url}/PurchaseFreemiumCredit`, {}, this.options);
  }

  AddEbayAccount(params) {
    return this.http.post(`/${this.url}/AddEbayAccount`, params, this.options);
  }

  AddAmazonAccount(params: AddAmazonAccountParams) {
    return this.http.post(`/${this.url}/account`, params, this.options);
  }

  UpdateAccountSettings(payload) {
    return this.http.post(`/${this.url}/UpdateAccountSettings`, payload , this.options);
  }

  getStatesOfCountry(isoCode) {
    return this.http.get(`/.netlify/functions/getStatesOfCountry?isoCode=${isoCode}`);
  }

  getAllCountries() {
    return this.http.get(`/.netlify/functions/getAllCountries`);
  }

  public CheckDuplicateValue(fieldName,value) {
    return this.http.get(`/${this.url}/CheckDuplicateValue?FieldName=${fieldName}&Value=${value}`,
      this.options
    );
  }

  AddShopifyAccount(payload) {
    return this.http.post(`/${this.url}/AddShopifyAcct`, payload , this.options);
  }

  addMarketing(payload){
    return this.http.post(`/${this.url}/addMarketing`, payload , this.options);
  }

  retryChargeCard(invoiceId){
    return this.http.post(`/${this.url}/RetryChargeCard`, {invoiceId} , this.options);
  }

  addWayFairAccount(payload){
    return this.http.post(`/${this.url}/AddWayFairAccount`, payload , this.options);
  }
  addWooCommerceAccount(payload:AddWooCommerceAccount){
    return this.http.post(`/${this.url}/AddWooCommerceAccount`, payload , this.options);
  }

  addBigCommerceAccount(payload:AddBigCommercePayload){
    return this.http.post(`/${this.url}/AddBigCommerceAccount`, payload , this.options);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

}
